import type { MobxDocument } from '../firestore-mobx'
import type { FirebaseRepository } from './FirebaseRepository'
import { computed, makeObservable, reaction, runInAction } from 'mobx'
import {
  empty,
  SectionPaymentMethod,
  SectionState,
  type FirestoreSection,
} from '../firestore/Section/schema'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import { PublicUser } from './PublicUser'
import type { SectionAssignment } from './SectionAssignment'
import type { OrganizationInvoiceStatus } from '../types'
import { AssignmentState, SectionRequestState } from '../types'
import type { SectionRequest } from './SectionRequest'
import { DateTime } from 'luxon'
import type { LoadingState } from '../util/guardOn'
import type { SectionProduct } from './SectionProduct'
import { fetchMostRecentSectionProduct } from '../firestore/SectionProduct'
import { fetchOrganizationCustomProducts } from '../firestore/OrganizationCustomProduct'

export class Section extends ObservableModelWithDecorators<FirestoreSection> {
  instructor: PublicUser = PublicUser.empty(this.repository)

  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSection>
  ) {
    super(repository, doc)

    // Watch for changes to the instructorUserId, to update the instructor.
    reaction(
      () => ({
        exists: this.exists,
        instructorUserId: this.data?.instructorUserId,
      }),
      ({ exists, instructorUserId }) => {
        if (!exists) return
        if (!instructorUserId) return
        runInAction(() => {
          this.instructor = this.repository.userStore.getUser(instructorUserId)
        })
      },
      { fireImmediately: true }
    )
    makeObservable(this)
  }

  @computed
  get sectionEndDateTime(): DateTime | undefined {
    return this.data.sectionEndDate
      ? DateTime.fromJSDate(this.data.sectionEndDate)
      : undefined
  }

  @computed
  get sectionStartDateTime(): DateTime | undefined {
    return this.data.sectionStartDate
      ? DateTime.fromJSDate(this.data.sectionStartDate)
      : undefined
  }

  @computed
  get students() {
    return this.repository.userStore.getUsers(this.data.userIds)
  }

  @computed
  get studentsIncludingRemovedIds() {
    return [...this.data.userIds, ...(this.data.removedUserIds ?? [])]
  }

  @computed
  get studentsIncludingRemoved() {
    return this.repository.userStore.getUsers(this.studentsIncludingRemovedIds)
  }

  @computed
  get studentsLoaded() {
    if (this.data.userIds.length === 0) return true

    return this.studentsIncludingRemoved.every((user) => user.isLoaded)
  }

  @computed
  get userCount() {
    return this.data.userIds.length
  }

  @computed
  get removedUserCount() {
    return this.data.removedUserIds?.length ?? 0
  }

  @computed
  get studentsIncludingRemovedCount() {
    return this.userCount + this.removedUserCount
  }

  @computed
  get isLtiCourse() {
    return !!this.data.canvasCourseId || !!this.data.ltiCourseId
  }

  @computed
  get isTest() {
    return this.data.isTest ?? false
  }

  @computed
  get lmsName(): 'Canvas' | 'D2L' | null {
    if (this.data.canvasCourseId) return 'Canvas'
    if (this.data.ltiCourseId) return 'D2L'
    return null
  }

  @computed
  get sectionLabelWithClass() {
    return `${this.data.className}: ${this.data.sectionName}`
  }

  @computed
  get sectionPaymentMethodWithDefault() {
    return this.data.sectionPaymentMethod ?? SectionPaymentMethod.token
  }

  @computed
  get usesCodelessAccess() {
    return (
      this.sectionPaymentMethodWithDefault ===
      SectionPaymentMethod.sectionPassCodelessAccess
    )
  }

  @computed
  get usesSectionPass() {
    return [
      SectionPaymentMethod.sectionPassStripe,
      SectionPaymentMethod.sectionPassStripeCoupon,
      SectionPaymentMethod.sectionPassCoupon,
      SectionPaymentMethod.sectionPassCodelessAccess,
    ].includes(this.sectionPaymentMethodWithDefault)
  }

  /**
   * True while the section has a "free access window" that has not yet passed.
   * During this window students may access materials without paying (BL-4581).
   */
  @computed
  get hasActiveFreeAccessWindow() {
    const freeAccessUntil = this.data.freeAccessUntil
    return !!freeAccessUntil && Date.now() < freeAccessUntil.getTime()
  }

  @computed
  get usesStripePurchase() {
    return [
      SectionPaymentMethod.token,
      SectionPaymentMethod.sectionPassStripe,
      SectionPaymentMethod.sectionPassStripeCoupon,
      SectionPaymentMethod.sectionPassCodelessAccess,
    ].includes(this.sectionPaymentMethodWithDefault)
  }

  @computed
  get usesSectionPassStripePayment() {
    if (!this.data.sectionPaymentMethod) return false
    return [
      SectionPaymentMethod.sectionPassStripe,
      SectionPaymentMethod.sectionPassStripeCoupon,
    ].includes(this.data.sectionPaymentMethod)
  }

  @computed
  get usesCouponCodes() {
    return [
      SectionPaymentMethod.sectionPassCoupon,
      SectionPaymentMethod.sectionPassStripeCoupon,
    ].includes(this.sectionPaymentMethodWithDefault)
  }

  /**
   * if section is shareable and the current user is not the instructor or a TA to the instructor,
   * then the shared section is read-only
   */
  @computed
  get isReadOnlyShareableSectionForCurrentUser() {
    if (!this.data) return true

    const { instructorUserId, shareable, collaboratorRWUserIds } = this.data
    const { breakoutUser } = this.repository

    if (!shareable) return false // if not shareable its not read only
    if (!breakoutUser) return true // if no current user, cant be instructor

    if (breakoutUser.isCorre) return false // CORRE are never read-only locked

    return (
      breakoutUser.uid !== instructorUserId &&
      !breakoutUser.isAssistantOfInstructor(instructorUserId) &&
      !(collaboratorRWUserIds || []).includes(breakoutUser.uid)
    )
  }

  getSectionStatus({
    assignments,
    latestInvoiceRequest,
  }: {
    assignments: SectionAssignment[]
    latestInvoiceRequest: SectionRequest | null
  }): SectionStatus {
    const sectionStart = this.data.sectionStartDate
    const sectionEnd = this.data.sectionEndDate
    const now = new Date()
    const publishedAssignments = assignments.filter(
      (a) => a.data.assignmentState > AssignmentState.draft
    )

    if (this.data.sectionState === SectionState.completed) {
      return SectionStatus.completed
    }

    if (
      latestInvoiceRequest?.data.sectionRequestState ===
      SectionRequestState.pending
    ) {
      return SectionStatus.invoicePending
    }

    if (
      latestInvoiceRequest?.data.sectionRequestState ===
      SectionRequestState.rejected
    ) {
      return SectionStatus.invoiceRejected
    }

    if (publishedAssignments.length === 0) {
      return SectionStatus.draft
    }

    if (sectionEnd) {
      if (now > sectionEnd) {
        // In theory our backend trigger should have enforced this.data.sectionState === SectionState.completed, but to ensure freshness we include this clause.
        return SectionStatus.completed
      }
    } else {
      if (publishedAssignments.every((a) => a.expiresAtIsInPast)) {
        const latestExpiresAt = Math.max(
          ...publishedAssignments.map((a) => a.data.expiresAt?.getTime() ?? 0)
        )

        const oneWeekMs = 7 * 24 * 60 * 60 * 1000

        if (Date.now() - latestExpiresAt >= oneWeekMs) {
          return SectionStatus.completed
        }
      }
    }

    if (sectionStart) {
      if (now < sectionStart) {
        return SectionStatus.notStarted
      }
    } else {
      if (publishedAssignments.every((a) => a.assignedAtIsInFuture)) {
        return SectionStatus.notStarted
      }
    }

    return SectionStatus.inProgress
  }

  canInviteStudents({
    sectionAssignments,
    sectionStatus,
  }: {
    sectionAssignments: SectionAssignment[]
    sectionStatus: SectionStatus
  }) {
    if (this.isReadOnlyShareableSectionForCurrentUser) return false

    if (this.isLtiCourse) return false

    if (sectionStatus === SectionStatus.completed) return false

    if (sectionStatus === SectionStatus.invoicePending) return false

    if (
      sectionAssignments.every((a) => !a.isActive) &&
      this.studentsIncludingRemovedCount === 0
    )
      return false

    return true
  }

  cannotInviteStudentsReason({
    sectionStatus,
  }: {
    sectionStatus: SectionStatus
  }) {
    if (this.isReadOnlyShareableSectionForCurrentUser)
      return 'invites_read_only' as const

    if (this.isLtiCourse) return 'invites_lti_course' as const

    if (sectionStatus === SectionStatus.completed)
      return 'invites_class_ended' as const

    if (sectionStatus === SectionStatus.invoicePending)
      return 'invites_invoice_pending' as const

    return 'needs_published_assignment' as const
  }

  isInvoiced({
    invoiceRequests,
    organizationInvoiceStatus,
  }: {
    invoiceRequests: SectionRequest[] | undefined
    organizationInvoiceStatus: OrganizationInvoiceStatus | undefined
  }) {
    // Codeless access sections are never invoiced — billing is handled
    // by the institution through the bookstore, not through our system
    if (this.usesCodelessAccess) return false

    const hasApprovedInvoiceRequest = invoiceRequests?.find(
      (r) =>
        r.data.sectionId === this.id &&
        r.data.sectionRequestState === SectionRequestState.approved
    )

    void organizationInvoiceStatus
    const sectionIsManuallyInvoiced = this.data.invoiced ?? false

    return sectionIsManuallyInvoiced || hasApprovedInvoiceRequest
  }

  @computed
  get sectionProduct() {
    const user = this.repository.breakoutUser
    const product = user
      ? user.getMostRecentSectionProduct(this.id)
      : ({
          isLoaded: true,
          isLoading: false,
          value: null,
        } as LoadingState<SectionProduct | null>)

    return product
  }

  async fetchSectionPrice() {
    const expectedStudents = this.data.expectedStudents

    const sectionProduct = await fetchMostRecentSectionProduct(
      this.repository,
      {
        sectionId: this.id,
      }
    )

    let isProduct = false
    let price: number | null = null

    // no product, use the basic price
    if (!sectionProduct) {
      await this.repository.pricing.ready()
      price = this.repository.pricing.data.tokenPrice * (expectedStudents || 0)
    } else {
      //
      const isCustom = sectionProduct.data.customProduct
      const organizationId = this.data.organizationId

      if (isCustom && organizationId) {
        // fetch custom products for organization
        const products = await fetchOrganizationCustomProducts(
          this.repository,
          organizationId
        )
        const customProduct = products.find(
          (p) => p.id === sectionProduct.data.productId
        )

        if (customProduct && expectedStudents) {
          isProduct = true
          price = customProduct.data.sectionPassPrice * expectedStudents
        }
      } else {
        const products = this.repository.breakoutUser!.productsCollection

        await products.ready()

        const product = products.models.find(
          (p) => p.id === sectionProduct.data.productId
        )

        if (product && expectedStudents) {
          isProduct = true
          price = product.data.sectionPassPrice * expectedStudents
        }
      }
    }

    return { isProduct, price }
  }
}

export enum SectionStatus {
  draft = 'draft',
  notStarted = 'not_started',
  inProgress = 'in_progress',
  completed = 'completed',
  invoiceRejected = 'invoice_rejected',
  invoicePending = 'invoice_pending',
}
